import React, { useState, useEffect, useRef } from "react";
import FetchData, { CreateEntry, UpdateEntry, GetListAsync } from '../../../providers/dataProvider';
import { CheckNull, CurrencyConvertion, FormatValueDecimal, QatarConvertion } from "../../../utils/Validate";
import SaveSuccessModel from "../../common/SaveSuccessModel";
import urls from "../../../constants";
import SelectedConfiguration from "./SelectedConfiguration";
import ProductQuantityTable from "./ProductQuantityTable";
import fallbackImg from "../../../assets/images/product1.png";
import { buildColorChargeRows, toRemainingTransactionColorCharges, toTransactionColorCharges } from "../../../utils/colorCharges";

const FACTORY_RELEASE_STORAGE_KEY = "factoryReleaseRecordId";

const FactoryOrderQuantityEntry = ({ setOrderProductTab, values, setValues, orderProductTab, currencyDetail, factoryReleaseRecordId, refreshFactoryOrderData, refreshFactoryOrderMarkData, factoryOrderRecord, releaseForm, isFactoryOrderEditMode, isFactoryOrderReleased, isFactoryOrderLocked, isLockedForPendingApproval, onRejectedReleaseUpdated }) => {
    // All state management here
    const [productSubmit, setProductSubmit] = useState(false)
    const [productVariant, setProductVariant] = useState([]);
    const [attributeDetails, setattributeDetails] = useState([]);
    const [variantsArray, setvariantsarray] = useState([])
    const [markCreateMessage, setMarkCreateMessage] = useState(null);
    const [createMsg, setCreateMsg] = useState(null)
    const [successshow, setSuccessShow] = useState(false)
    const [addProduct, setAddProduct] = useState([])
    const [productConfigVariant, setProductConfigVariant] = useState([]);
    const [markDetails, setMarkDetails] = useState(null);
    const [orderMarkDetails, setOrderMarkDetails] = useState(null);
    const [configSpecs, setConfigSpecs] = useState([]);
    const [activeTab, setActiveTab] = useState("nav-sizes");
    const [markErrorShow, setMarkErrorShow] = useState({ show: false, errorMessage: "" });
    const [factoryAvailableItems, setFactoryAvailableItems] = useState(null);
    const lastSubmittedReleaseItemsRef = useRef([]);
    // A refresh can return before the factory-order date update is visible in
    // its response. Keep the date the user just saved so that refresh does
    // not put the old date back into the open item editor.
    const savedRequestedDeliveryDatesRef = useRef(new Map());
    const hasRefreshedFactoryOrderAfterSaveRef = useRef(false);
    const { data: productVariants, loading: productVariantsLoading, error: productVariantsError } =
        FetchData("productsvariants", { id: markDetails?.groupId || orderProductTab?.data?.groupId });

    const { data: productsconfig, loading: productConfigLoading, error: productConfigError } =
        FetchData("productsspecification", { id: orderProductTab?.data?.productId });


    const isExistingMark = Boolean(orderMarkDetails?.id);
    const isFactoryOrderEdit = Boolean(factoryReleaseRecordId);
    const factoryOrderStatus = String(factoryOrderRecord?.status ?? "").toLowerCase();
    const isRecordLockedForEdit = factoryOrderRecord?.isLockedForEdit === true && (
        Number(factoryOrderRecord?.status) === 1 ||
        Number(factoryOrderRecord?.status) === 3 ||
        factoryOrderStatus === "released" ||
        factoryOrderStatus === "pending approval" ||
        factoryOrderStatus === "pendingapproval"
    );
    const isEditLocked = Boolean(isFactoryOrderLocked || isRecordLockedForEdit || isFactoryOrderReleased);
    const showPendingApprovalLockMessage = Boolean(
        isLockedForPendingApproval ||
        (isRecordLockedForEdit && (
            Number(factoryOrderRecord?.status) === 3 ||
            factoryOrderStatus === "pending approval" ||
            factoryOrderStatus === "pendingapproval"
        ))
    );

    const useFactoryReleaseColorSource =
        isFactoryOrderEditMode || Boolean(factoryOrderRecord?.id);
    const isInCurrentOrder = (item) => item?.isInCurrentOrder !== false;
    const activeMarkLookupKey = [
        orderProductTab?.data?.id,
        orderProductTab?.data?.quoteProductId,
        orderProductTab?.data?.quoteProductItemId,
        orderProductTab?.data?.orderProductId,
        orderProductTab?.data?.productId,
    ].filter((value) => value !== null && value !== undefined && value !== "").join("|");
    const productsLookupKey = (values?.products || [])
        .map((item) => [
            item?.id,
            item?.quoteProductId,
            item?.quoteProductItemId,
            item?.orderProductId,
        ].filter((value) => value !== null && value !== undefined && value !== "").join(":"))
        .join("|");

    const getConfigList = (data) => {
        if (Array.isArray(data)) return data;
        return data?.payload || data?.data || [];
    };

    const resolveMarkKeys = (data) => {
        const quoteProductId =
            data?.quoteProductId ??
            data?.quoteProductItemId ??
            data?.orderProductId ??
            data?.id ??
            0;

        const orderProductId =
            data?.orderProductId ??
            data?.id ??
            data?.quoteProductItemId ??
            data?.quoteProductId ??
            0;

        return { quoteProductId, orderProductId };
    };

    const matchesAnyKey = (item, keys = []) =>
        keys.some((key) =>
            key !== null &&
            key !== undefined &&
            [item?.quoteProductId, item?.quoteProductItemId, item?.orderProductId, item?.id]
                .some((value) => String(value) === String(key))
        );

    const safeNumber = (value, fallback = 0) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : fallback;
    };

    const getDeliveryDateKeys = (item) => [
        item?.customerOrderProductItemId,
        item?.quoteProductItemId,
        item?.orderProductItemId,
        item?.customerOrderItemId,
        item?.baseItemId,
        item?.id,
    ]
        .filter((value) => value !== null && value !== undefined && value !== "")
        .map(String);

    const getSavedRequestedDeliveryDate = (item) =>
        getDeliveryDateKeys(item)
            .map((key) => savedRequestedDeliveryDatesRef.current.get(key))
            .find((date) => date !== undefined);

    const getFactoryAvailableRows = (response) => {
        const root = response?.data ?? response ?? {};
        const nested = root?.data ?? root?.payload ?? root;

        if (Array.isArray(nested)) return nested;
        if (Array.isArray(nested?.productItems)) return nested.productItems;
        if (Array.isArray(nested?.items)) return nested.items;
        if (Array.isArray(nested?.payload)) return nested.payload;
        if (Array.isArray(nested?.data)) return nested.data;

        return [];
    };

    const getFactoryReleaseRows = (response) => {
        const root = response?.data ?? response ?? {};
        const nested = root?.data ?? root?.payload ?? root;

        if (Array.isArray(nested)) return nested;
        if (Array.isArray(nested?.items)) return nested.items;
        if (Array.isArray(nested?.productItems)) return nested.productItems;
        if (Array.isArray(nested?.payload)) return nested.payload;
        if (Array.isArray(nested?.data)) return nested.data;
        if (Array.isArray(nested?.products)) {
            return nested.products.flatMap((product) =>
                Array.isArray(product?.productItems) && product.productItems.length > 0
                    ? product.productItems
                    : [product]
            );
        }

        return [];
    };

    const toLocalDateIso = (value, fallback = null) => {
        const dateValue = value || fallback;
        if (!dateValue) return null;

        if (typeof dateValue === "string") {
            const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateValue);
            if (match) {
                return `${match[1]}-${match[2]}-${match[3]}T00:00:00`;
            }
        }

        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return null;

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}T00:00:00`;
    };

    const getRowMatchKeys = (item) => [
        item?.customerOrderItemId,
        item?.customerOrderProductItemId,
        item?.quoteProductItemId,
        item?.orderProductItemId,
        item?.quoteProductId,
        item?.id,
    ].filter((value) => value !== null && value !== undefined && value !== "");

    const getItemIdentity = (item = {}) => ({

        id: item?.id ?? item?.quoteProductItemId ?? item?.customerOrderProductItemId ?? 0,
        orderProductId: item?.orderProductId ?? item?.customerOrderProductItemId ?? item?.quoteProductItemId ?? item?.id ?? 0,
        quoteProductItemId: item?.quoteProductItemId ?? item?.customerOrderProductItemId ?? item?.id ?? 0,
        customerOrderProductItemId: item?.customerOrderProductItemId ?? item?.quoteProductItemId ?? item?.id ?? 0,
        customerOrderItemId: item?.customerOrderItemId ?? item?.customerOrderProductItemId ?? item?.quoteProductItemId ?? item?.id ?? 0,
    });

const getOrderQuantity = (item) => {
    return Number(
        item?.currentOrderQuantity ??
        item?.currentOrderedQuantity ??
        item?.lpoQuantity ??
        item?.quotedQuantity ??
        item?.quantity ??
        item?.qtyValues ??
        0
    );
};

const getReleasedToFactoryQuantity = (item) => {
    return Number(
        item?.factoryReleasedQuantity ??
        item?.releasedQuantity ??
        item?.releasedToFactoryQuantity ??
        0
    );
};

const getPendingReleaseQty = (item) => {
  const orderQty = Number(
    item?.currentOrderQuantity ??
    item?.currentOrderedQuantity ??
    item?.lpoQuantity ??
    item?.quantity ??
    item?.qtyValues ??
    0
  );

  const releasedQty = Number(
    item?.factoryReleasedQuantity ??
    item?.releasedQuantity ??
    item?.currentReleasedQuantity ??
    0
  );

  return Math.max(orderQty - releasedQty, 0);
};
    const isFactoryItemAvailable = (item) => getPendingReleaseQty(item) > 0;

    useEffect(() => {

        if (productVariants?.length > 0) {
            const checkGroupVariant = productVariants?.filter(
                (item) => item?.productSubGroupId === orderProductTab?.data?.productSubGroupId && item?.isActive === true
            )
            const resolved = checkGroupVariant?.length > 0
                ? checkGroupVariant
                : productVariants?.filter((item) => item?.isActive === true && (item?.productSubGroupId === 0 || item?.productSubGroupId === null));

            setProductVariant(resolved);
            setProductConfigVariant(resolved);
        }
    }, [productVariants, orderProductTab])

    const loadFactoryAvailableItems = async (customerOrderId) => {
        if (!customerOrderId || isFactoryOrderEdit) {
            setFactoryAvailableItems(null);
            return;
        }

        const response = await GetListAsync("factoryorderitem", {
            customerOrderId,
        });

        setFactoryAvailableItems(response?.data || null);
    };

    useEffect(() => {
        loadFactoryAvailableItems(values?.id);
    }, [values?.id, isFactoryOrderEdit]);

    useEffect(() => {
        if (productConfigLoading) {
            return;
        }

        const GetMarkDetails = async () => {
            const lookupKeys = resolveMarkKeys(orderProductTab?.data || {});
            const productFilter = values?.products?.find((item) =>
                matchesAnyKey(item, [lookupKeys.quoteProductId, lookupKeys.orderProductId])
            );
            let orderMarkDetailsData = null;
            if (productFilter?.id) {
                const ordermarkData = await GetListAsync("orderproductmark", { id: productFilter?.id })
                orderMarkDetailsData = ordermarkData?.data?.[0];
            }

            const hasQuoteMarkId = Number(lookupKeys.quoteProductId) > 0;
            const shouldLoadQuoteMark = hasQuoteMarkId && (!isFactoryOrderEdit || !orderMarkDetailsData);
            const markData = shouldLoadQuoteMark
                ? await GetListAsync("productmark", { id: lookupKeys.quoteProductId })
                : null;
            const markDetailsData =
                orderMarkDetailsData ||
                markData?.data?.[0] ||
                orderProductTab?.data ||
                null;

            setMarkDetails(markDetailsData);
            setOrderMarkDetails(orderMarkDetailsData)

            const productConfigList = getConfigList(productsconfig);
            const productFilterObj = productConfigList?.filter((config) => config?.productId === (orderProductTab?.data?.productId ?? orderProductTab?.data?.id))
            let arrayAttribute = [];
            const specificationsToUse = orderMarkDetailsData?.productSpecifications || markDetailsData?.productSpecifications;
            specificationsToUse?.forEach((spec) => {
                const findAttribute = productFilterObj
                    ?.map((productItem) => productItem?.productSpecMaps)
                    ?.flat()
                    ?.filter((specItem) => specItem?.attributeId === spec?.specificationId);
                const findvalue = findAttribute?.[0]?.productSpecValueMaps?.filter((valuemap) => valuemap?.id === spec?.specificationValueId)

                arrayAttribute.push({
                    ...spec,
                    attributeName: findAttribute?.[0]?.attributeName,
                    attributeValue: findvalue?.[0]?.valueName,
                    groupName: findAttribute?.[0]?.groupName || "MODEL CONFIG",
                })
            })
            setattributeDetails(arrayAttribute)
        }
        if (productsconfig) {
            GetMarkDetails();
        }
    }, [productsconfig, productConfigLoading, isFactoryOrderEdit, activeMarkLookupKey, productsLookupKey])

    useEffect(() => {
        if (attributeDetails?.length > 0) {
            setConfigSpecs(attributeDetails);
        }
    }, [attributeDetails])
    useEffect(() => {
        if (markDetails && productVariant.length > 0) {
            let addProductArray = [];
            let productVariantsArray = [];
            const quoteRows = markDetails?.productItems || [];
            const orderMarkRows = orderMarkDetails?.productItems?.length > 0
                ? orderMarkDetails.productItems
                : (markDetails?.productItems || []);
            const availableRows = getFactoryAvailableRows(factoryAvailableItems);
            const releaseRows = getFactoryReleaseRows(factoryOrderRecord);
            const baseRows = availableRows?.length > 0 ? availableRows : orderMarkRows;

            const pendingRows = baseRows.map((baseItem) => {
                const baseKeys = getRowMatchKeys(baseItem);
                const orderMarkItem = orderMarkRows.find((item) =>
                    getRowMatchKeys(item).some((key) =>
                        baseKeys.some((baseKey) => String(baseKey) === String(key))
                    )
                );
                const quoteItem = quoteRows.find((item) =>
                    getRowMatchKeys(item).some((key) =>
                        baseKeys.some((baseKey) => String(baseKey) === String(key))
                    )
                );
                const orderQty = Number(
                    baseItem?.currentOrderQuantity ??
                    orderMarkItem?.currentOrderQuantity ??
                    orderMarkItem?.currentOrderedQuantity ??
                    quoteItem?.currentOrderQuantity ??
                    quoteItem?.currentOrderedQuantity ??
                    orderMarkItem?.lpoQuantity ??
                    quoteItem?.lpoQuantity ??
                    0
                );
                const releasedQty = Number(
                    baseItem?.factoryReleasedQuantity ??
                    baseItem?.releasedQuantity ??
                    orderMarkItem?.factoryReleasedQuantity ??
                    orderMarkItem?.releasedQuantity ??
                    quoteItem?.factoryReleasedQuantity ??
                    quoteItem?.releasedQuantity ??
                    0
                );

                const pendingQty = Math.max(orderQty - releasedQty, 0);

                return {
                    ...baseItem,
                    ...(orderMarkItem || {}),
                    ...(quoteItem || {}),
                    baseItemId: baseItem?.id ?? 0,
                    customerOrderItemId: baseItem?.id ?? orderMarkItem?.customerOrderItemId ?? quoteItem?.customerOrderItemId ?? 0,
                    productItemVariants:
                        quoteItem?.productItemVariants?.length > 0
                            ? quoteItem.productItemVariants
                            : (
                                orderMarkItem?.productItemVariants?.length > 0
                                    ? orderMarkItem.productItemVariants
                                    : (baseItem?.productItemVariants || [])
                            ),
                    lpoQuantity: orderQty,
                    currentOrderQuantity: orderQty,
                    currentOrderedQuantity: orderQty,
                    releasedQuantity: releasedQty,
                    factoryReleasedQuantity: releasedQty,
                    currentReleasedQuantity: pendingQty,
                    __isSaved: false,
                    __quoteItem: quoteItem || orderMarkItem || baseItem,
                };
            });

            const editRows = orderMarkRows.map((orderItem) => {
                const releaseItem = releaseRows.find((item) =>
                    getRowMatchKeys(item).some((key) =>
                        getRowMatchKeys(orderItem).some((orderKey) => String(orderKey) === String(key))
                    )
                );
        const productItems = factoryOrderRecord?.products ?? [];

        const currentProduct = productItems?.find(
            item =>
                Number(item?.customerOrderProductId) ===
                Number(orderItem?.orderProductId ?? orderItem?.customerOrderProductId ?? orderItem?.quoteProductId)
        );
        const currentProductItem = currentProduct?.productItems?.find((item) =>
            getRowMatchKeys(item).some((key) =>
                getRowMatchKeys(orderItem).some((orderKey) => String(orderKey) === String(key))
            )
        );

        const hasAnyItemId = currentProduct?.productItems?.some(
            item => Number(item?.id ?? 0) > 0
        );

        const hasFactoryReleasedQty = currentProduct?.productItems.some(
            item => Number(item?.factoryReleasedQuantity ?? 0) > 0
        );
      
        const orderedQty = Number(
            orderItem?.currentOrderedQuantity ??
            orderItem?.currentOrderQuantity ??
            orderItem?.lpoQuantity ??
            orderItem?.customerOrderedQuantity ??
            orderItem?.orderedQuantity ??
            orderItem?.quantity ??
            0
        );

        const balanceQty = Number(
            currentProductItem?.balanceQuantity ?? 0
        );

        const currentReleaseQty = hasAnyItemId
            ? Number(currentProductItem?.id ?? 0) > 0
                ? Number(currentProductItem?.currentReleaseQuantity ?? 0)
                : 0
            : hasFactoryReleasedQty
                ? balanceQty
                : orderedQty;
                        const releasedHistoryQty = Number(
                            orderItem?.releasedQuantity ??
                            orderItem?.factoryReleasedQuantity ??
                            releaseItem?.releasedQuantity ??
                            releaseItem?.factoryReleasedQuantity ??
                            0
                        );

                return {
                    ...orderItem,
                    ...getItemIdentity(orderItem),
                    __isSaved: true,
                    __quoteItem: orderItem,
                    __releaseItem: releaseItem || null,
                    requestedDeliveryDate:
                        releaseItem?.requestedDeliveryDate ??
                        releaseItem?.effectiveRequestedDeliveryDate ??
                        currentProductItem?.requestedDeliveryDate ??
                        currentProductItem?.effectiveRequestedDeliveryDate ??
                        currentProduct?.requestedDeliveryDate ??
                        currentProduct?.effectiveRequestedDeliveryDate ??
                        orderItem?.requestedDeliveryDate,
                    currentReleasedQuantity: currentReleaseQty,
                    factoryReleasedQuantity: releasedHistoryQty,
                    releasedQuantity: releasedHistoryQty,
                    isInCurrentOrder: true,
                };
            });

            const rowsToUse = isFactoryOrderEdit
                ? editRows
                : pendingRows;
            const visibleRows = isFactoryOrderEdit
                // The edit response includes every size for the mark. Only
                // render sizes assigned a quantity in this factory release.
                ? rowsToUse.filter((item) => Number(item?.currentReleasedQuantity ?? 0) > 0)
                : rowsToUse.filter((item) => getPendingReleaseQty(item) > 0);
                
                if (visibleRows?.length > 0) {
                visibleRows?.map((prodItem, prodIndex) => {
                const pendingReleaseQty = getPendingReleaseQty(prodItem);
                const inCurrentOrder = isInCurrentOrder(prodItem);

                    if (!prodItem.__isSaved) {
                        const quoteQtyValue = Number(
                            prodItem?.currentOrderQuantity ??
                            prodItem?.currentOrderedQuantity ??
                            prodItem?.lpoQuantity ??
                            prodItem?.quantity ??
                            0
                        );
                        const currentReleasedValue = isFactoryOrderEdit ? 0 : (inCurrentOrder ? pendingReleaseQty : 0);

                        const convertedUnitPrice = CurrencyConvertion(
                            Number(prodItem?.unitPrice) || 0,
                            Number(values?.conversionRate) || 1,
                            currencyDetail?.shortCode
                        );

                        const unitPrice = Number(convertedUnitPrice) || 0;

                        const totalPriceValue = unitPrice * currentReleasedValue;

                        addProductArray.push({
                            sid: (prodIndex * 1) + 1,
                            ...getItemIdentity(prodItem),
                            customerOrderItemId: prodItem?.baseItemId ?? prodItem?.customerOrderItemId ?? prodItem?.id,
                            quoteProductId: prodItem?.quoteProductId ?? prodItem?.quoteProductItemId ?? prodItem?.id,
                            notes: prodItem?.notes,
                            specialDesignRequest: prodItem?.specialDesignRequest,
                            sdrCharges: prodItem?.sdrCharges,
                            qtyValues: quoteQtyValue,
                            currentOrderQuantity: quoteQtyValue,
                            customerOrderedQuantity: quoteQtyValue,
                            listPriceValues: prodItem?.listPrice,
                            unitPrice: prodItem?.unitPrice,
                            totalPrice: inCurrentOrder ? totalPriceValue : 0,
                            releasedQuantity: Number(prodItem?.releasedQuantity ?? 0),
                            lpoQuantity: quoteQtyValue,
                            currentReleasedQuantity: currentReleasedValue,
                            factoryReleasedQuantity: currentReleasedValue,
                            requestedDeliveryDate:
                                getSavedRequestedDeliveryDate(prodItem) ??
                                (isFactoryOrderEditMode
                                    ? (
                                        prodItem?.requestedDeliveryDate ??
                                        prodItem?.effectiveRequestedDeliveryDate ??
                                        orderProductTab?.data?.requestedDeliveryDate
                                    )
                                    : null) ??
                                releaseForm?.deliveryFromDate ??
                                releaseForm?.scheduledDeliveryDate ??
                                values?.scheduledDeliveryDate ??
                                null,
                            isInCurrentOrder: inCurrentOrder,
                            margin: prodItem?.margin ?? 0,
                            
                        });

                        prodItem?.productItemVariants?.map((varItem, varIndex) => {
                            const filterVar = productVariant?.filter(
                                (pvar) => pvar.variantId === varItem.variantId &&
                                    !urls?.HIDDEN_FIELDS_GROUP?.includes(pvar?.groupId)
                            );
                            if (filterVar && filterVar?.length > 0) {
                                productVariantsArray.push({
                                    sid: (prodIndex * 1) + 1,
                                    id: varItem.id,
                                    quoteProductItemId: prodItem.id,
                                    variantId: varItem?.variantId,
                                    variantValue: varItem?.variantValue,
                                    inputByUser: filterVar[0].inputByUser,
                                });
                            }
                        });
                    } else {
                        const quoteQtyValue = Number(
                            prodItem?.currentOrderQuantity ??
                            prodItem?.lpoQuantity ??
                            prodItem?.customerOrderedQuantity ??
                            prodItem?.orderedQuantity ??
                            prodItem?.quantity ??
                            0
                        );
                        const currentReleasedValue = isFactoryOrderEdit
                            ? Number(prodItem?.currentReleasedQuantity ?? 0)
                            : (inCurrentOrder ? pendingReleaseQty : 0);
                        const convertedUnitPrice = CurrencyConvertion(
                            Number(prodItem?.unitPrice) || 0,
                            Number(values?.conversionRate) || 1,
                            currencyDetail?.shortCode
                        );
                         const productItems = factoryOrderRecord?.products ?? [];

                        const currentProduct = productItems?.find(
                            item =>
                                Number(item?.customerOrderProductId) ===
                                Number(prodItem?.orderProductId ?? prodItem?.customerOrderProductId ?? prodItem?.quoteProductId)
                        );
                        const currentProductItem = currentProduct?.productItems?.find((item) =>
                            getRowMatchKeys(item).some((key) =>
                                getRowMatchKeys(prodItem).some((prodKey) => String(prodKey) === String(key))
                            )
                        );

                        const unitPrice = Number(convertedUnitPrice) || 0;

                        const totalPriceValue = unitPrice * currentReleasedValue;

                        addProductArray.push({
                            sid: (prodIndex * 1) + 1,
                            // ...getItemIdentity(prodItem),
                             id: prodItem?.id,
                            quoteProductId: prodItem?.quoteProductItemId ?? prodItem?.quoteProductId ?? prodItem?.id,
                            orderProductId: prodItem?.orderProductId,
                            quoteProductId: prodItem?.quoteProductId ?? prodItem?.quoteProductItemId ?? prodItem?.id,
                            notes: prodItem?.notes,
                            specialDesignRequest: prodItem?.specialDesignRequest,
                            sdrCharges: prodItem?.sdrCharges,
                            qtyValues: prodItem?.quoteQuantity ?? prodItem?.quotedQuantity ?? orderMarkDetails?.quotedQuantity,
                            currentOrderQuantity: Number(
                                prodItem?.currentOrderQuantity ??
                                prodItem?.currentOrderedQuantity ??
                                prodItem?.lpoQuantity ??
                                prodItem?.customerOrderedQuantity ??
                                prodItem?.orderedQuantity ??
                                prodItem?.quantity ??
                                0
                            ),
                            customerOrderedQuantity: Number(
                                prodItem?.customerOrderedQuantity ??
                                prodItem?.lpoQuantity ??
                                prodItem?.orderedQuantity ??
                                prodItem?.quantity ??
                                0
                            ),
                            listPriceValues: prodItem?.listPrice,
                            unitPrice: prodItem?.unitPrice,
                            totalPrice: inCurrentOrder ? Number(totalPriceValue) : 0,
                            releasedQuantity: Number(
                                prodItem?.releasedQuantity ??
                                prodItem?.factoryReleasedQuantity ??
                                0
                            ),
                            lpoQuantity: quoteQtyValue,
                            currentReleasedQuantity: currentReleasedValue,
                            factoryReleasedQuantity: currentReleasedValue,
                            requestedDeliveryDate:
                                getSavedRequestedDeliveryDate(prodItem) ??
                                (isFactoryOrderEditMode
                                    ? (
                                        currentProductItem?.requestedDeliveryDate ??
                                        currentProductItem?.effectiveRequestedDeliveryDate ??
                                        prodItem?.requestedDeliveryDate ??
                                        prodItem?.effectiveRequestedDeliveryDate ??
                                        orderProductTab?.data?.requestedDeliveryDate
                                    )
                                    : null) ??
                                releaseForm?.deliveryFromDate ??
                                releaseForm?.scheduledDeliveryDate ??
                                values?.scheduledDeliveryDate ??
                                null,
                            isInCurrentOrder: inCurrentOrder,
                            multiplier: prodItem?.multiplier,
                            margin: prodItem?.margin ?? 0,
                            orderedQty: prodItem?.customerOrderedQuantity
                        });

                        const variantsSource = prodItem?.productItemVariants?.length > 0
                            ? prodItem?.productItemVariants
                            : prodItem?.__quoteItem?.productItemVariants;

                        variantsSource?.map((varItem, varIndex) => {
                            const filterVar = productVariant?.filter(
                                (pvar) => pvar.variantId === varItem.variantId &&
                                    !urls?.HIDDEN_FIELDS_GROUP?.includes(pvar?.groupId)
                            );
                            if (filterVar && filterVar?.length > 0) {
                                productVariantsArray.push({
                                    sid: (prodIndex * 1) + 1,
                                    id: varItem.id,
                                    quoteProductItemId: varItem.orderProductItemId ?? varItem.quoteProductItemId ?? prodItem.id,
                                    variantId: varItem?.variantId,
                                    variantValue: varItem?.variantValue,
                                    inputByUser: filterVar[0].inputByUser,
                                });
                            }
                        });
                    }
                });

                if (productVariantsArray?.length > 0) {
                    setvariantsarray(productVariantsArray);
                }
                setAddProduct(addProductArray);
            }
        }
    }, [markDetails, productVariant, orderMarkDetails, factoryAvailableItems, factoryOrderRecord])

    useEffect(() => {
        if (productSubmit === true) {
           
            const createValue = {
                ...values,
                CreatedAt: new Date(),
                CreatedBy: localStorage.getItem("authidentity")
            };
            CreateEntry("order", createValue, setCreateMsg, "success", "Failed to create order");
        }
    }, [productSubmit])


    useEffect(() => {
        if (createMsg) {
            if (CheckNull(createMsg?.message) == "success") {
                if (typeof setValues === "function" && createMsg?.successData?.id) {
                    setValues((prev) => ({
                        ...prev,
                        ...createMsg.successData,
                        id: createMsg.successData.id,
                    }));
                }
                CreateProductOrder();
            } else {
                console.error("Order create failed:", createMsg);
            }
            setProductSubmit(false);
        }
    }, [createMsg])

    useEffect(() => {
        if (!markCreateMessage) return;

        const statusCode = markCreateMessage?.statusCode ?? markCreateMessage?.status;
        const isSuccess = statusCode === 200 || statusCode === 201 || statusCode === 204;

        if (isSuccess) {
            setSuccessShow(true);
            onRejectedReleaseUpdated?.();
            hasRefreshedFactoryOrderAfterSaveRef.current = false;
            const rawUpdatedMark = markCreateMessage?.successData;
            const savedFactoryOrderId =
                markCreateMessage?.successData?.id ||
                factoryReleaseRecordId ||
                values?.id ||
                localStorage.getItem(FACTORY_RELEASE_STORAGE_KEY) ||
                orderMarkDetails?.orderId ||
                "";

            if (savedFactoryOrderId) {
                localStorage.setItem(FACTORY_RELEASE_STORAGE_KEY, savedFactoryOrderId);
            }

            if (typeof refreshFactoryOrderMarkData === "function") {
                refreshFactoryOrderMarkData(
                    markCreateMessage?.successData?.id ||
                    orderMarkDetails?.id ||
                    factoryReleaseRecordId
                );
                hasRefreshedFactoryOrderAfterSaveRef.current = true;
            }
            if (typeof loadFactoryAvailableItems === "function") {
                loadFactoryAvailableItems(values?.id || orderMarkDetails?.orderId);
            }
            if (!hasRefreshedFactoryOrderAfterSaveRef.current && typeof refreshFactoryOrderData === "function") {
                refreshFactoryOrderData(savedFactoryOrderId);
            }
            if ((factoryReleaseRecordId || isExistingMark) && typeof setValues === "function") {
                const submittedItems = lastSubmittedReleaseItemsRef.current || [];

                if (submittedItems.length > 0) {
                    setAddProduct((previousRows) => previousRows.map((row) => {
                        const submitted = submittedItems.find((submittedItem) =>
                            getDeliveryDateKeys(row).some((key) =>
                                getDeliveryDateKeys(submittedItem).includes(key)
                            )
                        );

                        return submitted
                            ? {
                                ...row,
                                requestedDeliveryDate:
                                    submitted?.requestedDeliveryDate ?? row?.requestedDeliveryDate,
                            }
                            : row;
                    }));

                    setValues((prev) => {
                        const products = prev?.products || [];

                        const findSubmittedItem = (row) => submittedItems.find((submittedItem) =>
                            getDeliveryDateKeys(row).some((key) =>
                                getDeliveryDateKeys(submittedItem).includes(key)
                            )
                        );

                        const updatedProducts = products.map((product) => {
                            const submitted =
                                findSubmittedItem(product) ||
                                (product?.productItems || [])
                                    .map((productItem) => findSubmittedItem(productItem))
                                    .find(Boolean);
                            if (!submitted) return product;

                            const submittedQuantity = Number(submitted?.releasedQuantity || 0);
                            const requestedDeliveryDate =
                                submitted?.requestedDeliveryDate ?? product?.requestedDeliveryDate;

                            return {
                                ...product,
                                currentReleasedQuantity: submittedQuantity,
                                totalPrice: submitted?.totalPrice ?? product?.totalPrice,
                                requestedDeliveryDate,
                                // Used only until the next full factory-order
                                // response arrives; it prevents its stale
                                // product date from repainting this row.
                                __savedRequestedDeliveryDate: requestedDeliveryDate,
                                productItems: Array.isArray(product?.productItems)
                                    ? product.productItems.map((productItem) => {
                                        const submittedProductItem = findSubmittedItem(productItem);
                                        return submittedProductItem
                                            ? {
                                                ...productItem,
                                                requestedDeliveryDate:
                                                    submittedProductItem?.requestedDeliveryDate ?? productItem?.requestedDeliveryDate,
                                            }
                                            : productItem;
                                    })
                                    : product?.productItems,
                            };
                        });

                        const totalReleasedQuantity = updatedProducts.reduce(
                            (sum, p) => sum + (Number(p?.releasedQuantity) || 0),
                            0
                        );
                        const totalCustomerQuantity = updatedProducts.reduce(
                            (sum, p) =>
                                sum + (
                                    Number(
                                        p?.customerOrderedQuantity ??
                                        p?.lpoQuantity ??
                                        p?.orderedQuantity ??
                                        p?.quantity ??
                                        0
                                    ) || 0
                                ),
                            0
                        );

                        return {
                            ...prev,
                            products: updatedProducts,
                            releasedQuantity: totalReleasedQuantity,
                            orderQuantity: totalCustomerQuantity,
                        };
                    });

                    lastSubmittedReleaseItemsRef.current = [];
                }
            } else if (rawUpdatedMark?.id && typeof setValues === "function") {
                const totalReleasedQuantity = addProduct.reduce((sum, p) => sum + (Number(p?.releasedQuantity) || 0), 0);
                const totalLpoQuantity = addProduct.reduce((sum, p) => sum + (Number(p?.lpoQuantity) || 0), 0);

                const aggregateFields = {
                    releasedQuantity: totalReleasedQuantity,
                    orderQuantity: totalLpoQuantity,
                };

                const productItemsWithVariants = (rawUpdatedMark?.productItems || []).map((pi) => {
                    if (pi?.productItemVariants?.length > 0) return pi;

                    const submittedVariants = variantsArray
                        ?.filter((v) =>
                            v?.quoteProductItemId === pi?.quoteProductItemId ||
                            v?.quoteProductItemId === pi?.id
                        )
                        ?.map((v) => ({
                            id: v?.id ?? 0,
                            orderProductItemId: pi?.id,
                            variantId: v?.variantId,
                            variantValue: v?.variantValue,
                        })) || [];

                    return submittedVariants.length > 0
                        ? { ...pi, productItemVariants: submittedVariants }
                        : pi;
                });

                const updatedMark = { ...rawUpdatedMark, productItems: productItemsWithVariants };

                setValues((prev) => {
                    const products = prev?.products || [];
                    const exists = products.some((p) => p?.id === updatedMark.id);

                    const priceOverrides = {
                        listPrice: markDetails?.listPrice,
                        sellingPrice: markDetails?.sellingPrice,
                    };

                    const mergeMark = (oldItem, newItem) => {
                        const merged = { ...oldItem };
                        Object.keys(newItem || {}).forEach((key) => {
                            const value = newItem[key];
                            if (value !== null && value !== undefined && value !== "") {
                                merged[key] = value;
                            }
                        });
                        return merged;
                    };

                    let updatedProducts;
                    if (exists) {
                        updatedProducts = products.map((p) =>
                            p?.id === updatedMark.id
                                ? { ...mergeMark(p, updatedMark), ...aggregateFields, ...priceOverrides }
                                : p
                        );
                    } else {
                        updatedProducts = [
                            ...products,
                            {
                                ...updatedMark,
                                productName: updatedMark?.productName || orderProductTab?.data?.productName,
                                ...aggregateFields,
                                ...priceOverrides,
                            },
                        ];
                    }
                    return { ...prev, products: updatedProducts };
                });
            }
        } else {
            const apiErrors = markCreateMessage?.errors?.length > 0
                ? markCreateMessage.errors
                : markCreateMessage?.successData?.errors;

            const simpleText = apiErrors?.length > 0
                ? apiErrors.map(e => e.message || e.code).join(" ")
                : (CheckNull(markCreateMessage?.statusPhrase) || CheckNull(markCreateMessage?.message) || "Something went wrong. Please try again.");

            console.error("Order product mark save failed:", markCreateMessage);
            setSuccessShow(false);
            setMarkErrorShow({ show: true, errorMessage: simpleText });
        }
    }, [markCreateMessage])


    const CreateProductOrder = () => {
        if (isEditLocked) {
            setMarkErrorShow({
                show: true,
                errorMessage: showPendingApprovalLockMessage
                    ? "Factory release cannot be amended while approval is pending."
                    : "Released factory order cannot be updated."
            });
            return;
        }

        let submitdata = [];
        lastSubmittedReleaseItemsRef.current = [];

        const validProducts = addProduct.filter((productitem) => {
            if (productitem?.isInCurrentOrder === false) {
                return false;
            }

            const currentReleasedQuantity = Number(productitem?.currentReleasedQuantity);

            return isFactoryOrderEdit
                ? Number.isFinite(currentReleasedQuantity) && currentReleasedQuantity >= 0
                : currentReleasedQuantity > 0;
        });

        if (validProducts.length === 0) {
            setMarkErrorShow({
                show: true,
                errorMessage: isFactoryOrderEdit
                    ? "Each order item must reference a quote item and have a quantity of zero or more."
                    : "Each order item must reference a quote item and have a quantity greater than zero."
            });
            return;
        }

        const invalidQuantityProduct = validProducts.find((productitem) => {
            const currentReleasedQuantity = Number(productitem?.currentReleasedQuantity || 0);
            const lpoQuantity = Number(productitem?.lpoQuantity || 0);
            const releasedQuantity = Number(productitem?.releasedQuantity ?? productitem?.factoryReleasedQuantity ?? 0);
            const maxQuantity = isFactoryOrderReleased
                ? releasedQuantity
                : lpoQuantity;

            return currentReleasedQuantity > maxQuantity;
        });

        if (invalidQuantityProduct) {
            const maxQuantity = isFactoryOrderReleased
                ? Number(invalidQuantityProduct?.releasedQuantity ?? invalidQuantityProduct?.factoryReleasedQuantity ?? 0)
                : Number(invalidQuantityProduct?.lpoQuantity || 0);

            setMarkErrorShow({
                show: true,
                errorMessage: isFactoryOrderReleased
                    ? `Released factory order quantity cannot be greater than ${maxQuantity}.`
                    : `Factory order quantity has only ${maxQuantity} quantity available.`
            });
            return;
        }

        validProducts.map((productitem, productindex) => {
            let list = variantsArray.filter(function (data) {
                return data.sid === productitem?.sid
            })
               
            const variantslist = list?.map((element, index) => {
                let variantsdata = {
                    id: element?.id ?? 0,
                    orderProductItemId: element?.quoteProductItemId ?? 0,
                    variantId: element?.variantId,
                    variantValue: element?.variantValue
                }
                return variantsdata;
            });

            const filteredList = variantslist.filter(item =>
                urls?.TAG_REF_CONSTANT.includes(Number(item?.variantId))
            );
                let params = {
                    id: productitem?.id > 0 ? productitem?.id : 0,
                    customerOrderItemId:
                        productitem?.baseItemId ??
                        productitem?.customerOrderItemId ??
                        productitem?.id ??
                        0,
                    customerOrderProductItemId:
                        productitem?.customerOrderProductItemId ??
                        productitem?.quoteProductItemId ??
                        productitem?.orderProductItemId ??
                    productitem?.id ??
                    0,
                quoteProductItemId:
                    list?.[0]?.quoteProductItemId ??
                    productitem?.quoteProductId ??
                    productitem?.quoteProductItemId ??
                    productitem?.orderProductId ??
                    productitem?.id,
                orderProductId: productitem?.orderProductId,
                listPrice: productitem?.listPriceValues,
                lpoQuantity: productitem?.lpoQuantity,
                unitPrice: safeNumber(
                    FormatValueDecimal(QatarConvertion(productitem?.unitPrice, currencyDetail?.conversionRate, currencyDetail?.shortCode))
                ),
                margin: productitem?.margin,
                totalPrice: safeNumber(
                    FormatValueDecimal(QatarConvertion(productitem?.totalPrice, currencyDetail?.conversionRate, currencyDetail?.shortCode))
                ),
                 notes: productitem?.notes || "",
                 tagReference: filteredList?.[0]?.variantValue || "",
                specialDesignRequest: productitem?.specialDesignRequest,
                sdrCharges: safeNumber(
                    FormatValueDecimal(QatarConvertion(productitem?.sdrCharges, currencyDetail?.conversionRate, currencyDetail?.shortCode))
                ),
                releasedQuantity: Number(productitem?.currentReleasedQuantity || 0),
                requestedDeliveryDate: productitem?.requestedDeliveryDate,
                productItemVariants: variantslist,
            }
            submitdata.push(params)
        })
        lastSubmittedReleaseItemsRef.current = submitdata;
        submitdata.forEach((item) => {
            if (!item?.requestedDeliveryDate) return;
            getDeliveryDateKeys(item).forEach((key) => {
                savedRequestedDeliveryDatesRef.current.set(key, item.requestedDeliveryDate);
            });
        });

        const specificationsToSubmit = configSpecs?.length > 0
            ? configSpecs.map((spec) => ({
                orderProductId: spec?.orderProductId,
                specificationId: spec?.specificationId,
                specificationValueId: spec?.specificationValueId,
                specialValue: spec?.specialValue || "",
                specialCharges: spec?.specialCharges || 0,
            }))
            : (orderMarkDetails?.productSpecifications || markDetails?.productSpecifications);

        if (submitdata?.length === 0) {
            return;
        }

        const totalLpoQuantity = submitdata.reduce((sum, item) => sum + (Number(item.lpoQuantity) || 0), 0);

        const commonMarkData = {
            orderId: CheckNull(values?.id) !== "" ? values?.id : createMsg?.successData?.id,
            quoteProductId: markDetails?.id,
            markName: orderMarkDetails?.markName ?? markDetails?.markName,
            productId: orderMarkDetails?.productId ?? markDetails?.productId,
            groupId: orderMarkDetails?.groupId ?? markDetails?.groupId,
            groupName: orderMarkDetails?.groupName ?? markDetails?.groupName,
            productSubGroupId: orderMarkDetails?.productSubGroupId ?? markDetails?.productSubGroupId,
            quantity: markDetails?.quantity ?? 0,
            listPrice: markDetails?.listPrice,
            margin: markDetails?.margin ?? 0,
            salesMargin: markDetails?.margin ?? 0,
            sellingPrice: markDetails?.sellingPrice,
            lpoQuantity: totalLpoQuantity,
            productItems: submitdata,
            productSpecifications: specificationsToSubmit
        };
     

        const scheduledDeliveryDate = toLocalDateIso(releaseForm?.scheduledDeliveryDate) || toLocalDateIso(values?.scheduledDeliveryDate) || toLocalDateIso(new Date());
        const requestedDeliveryDate = toLocalDateIso(releaseForm?.deliveryFromDate) || scheduledDeliveryDate;

        const toIsoDate = (value, fallback = requestedDeliveryDate) => {
            return toLocalDateIso(value, fallback) || fallback;
        };

        const getDeliveryDatePatch = (value, fallback = requestedDeliveryDate) => {
            const requestedDate = toLocalDateIso(value, fallback);
            if (!requestedDate) {
                return { clearRequestedDeliveryDate: true };
            }

            return { requestedDeliveryDate: requestedDate };
        };

        const getCustomerOrderProductId = (item) =>
            item?.customerOrderProductId ??
            item?.orderProductId ??
            item?.quoteProductId ??
            item?.quoteProductItemId ??
            item?.id ??
            0;

        const getProductRequestedDeliveryDate = (item) => {
            const productId = getCustomerOrderProductId(item);
            const productDateSource = (values?.products || []).find((product) =>
                [product?.customerOrderProductId, product?.quoteProductId, product?.orderProductId, product?.id]
                    .filter((value) => value !== null && value !== undefined && value !== "")
                    .some((key) => String(key) === String(productId))
            );

            return (
                productDateSource?.requestedDeliveryDate ??
                productDateSource?.effectiveRequestedDeliveryDate ??
                orderProductTab?.data?.requestedDeliveryDate ??
                orderProductTab?.data?.effectiveRequestedDeliveryDate ??
                item?.requestedDeliveryDate ??
                item?.effectiveRequestedDeliveryDate ??
                requestedDeliveryDate
            );
        };

        const productDeliveryDates = Object.values(
            submitdata.reduce((acc, item) => {
                const customerOrderProductId = getCustomerOrderProductId(item);
                if (!customerOrderProductId || acc[customerOrderProductId]) {
                    return acc;
                }

                acc[customerOrderProductId] = {
                    customerOrderProductId,
                    ...getDeliveryDatePatch(getProductRequestedDeliveryDate(item)),
                };

                return acc;
            }, {})
        );

        const factoryOrderItems = submitdata.map((item) => ({
            customerOrderProductItemId:
                item?.baseItemId ??
                item?.customerOrderItemId ??
                item?.id ??
                0,
            quoteProductItemId: item?.quoteProductItemId ?? 0,
            orderProductItemId: item?.orderProductItemId ?? item?.orderProductId ?? 0,
            customerOrderItemId: item?.customerOrderItemId ?? item?.baseItemId ?? item?.id ?? 0,
            quantity: Number(item?.releasedQuantity || 0) || 0,
            notes: item?.notes || "",
            tagReference: item?.tagReference || "",
            ...getDeliveryDatePatch(item?.requestedDeliveryDate, toIsoDate(getProductRequestedDeliveryDate(item))),
        }));

        const commonFactoryOrderData = {
            facilityId: values?.facilityId ?? values?.manufacturingFacilityId ?? factoryOrderRecord?.facilityId ?? "",
            scheduledDeliveryDate,
            deliveryFromDate: requestedDeliveryDate,
            deliveryToDate: toLocalDateIso(releaseForm?.deliveryToDate),
            remarks: values?.remarks || "",
            additionalInfo: releaseForm?.additionalInfo || "",
            additionalCharges: Number(values?.additionalCharges || 0),
            deliveryCharges: Number(values?.deliveryCharges || 0),
            clearanceCharges: Number(values?.clearanceCharges || 0),
            specialColorCharges: Number(values?.specialColorCharges || 0),
            colorCharges: useFactoryReleaseColorSource
                ? toTransactionColorCharges(factoryOrderRecord?.colorCharges)
                : toRemainingTransactionColorCharges(buildColorChargeRows(values)),
            taxPercentage: Number(values?.taxPercentage || 0),
            tax: Number(values?.tax || 0),
            productDeliveryDates,
            items: factoryOrderItems,
        };

        if (isExistingMark) {
            const updateFactoryOrderData = {
                id: factoryReleaseRecordId || 0,
                ...commonFactoryOrderData,
            };

            if (factoryReleaseRecordId) {
                UpdateEntry(
                    "factoryOrder",
                    updateFactoryOrderData,
                    setMarkCreateMessage,
                    "Factory order updated successfully",
                    "Failed to update factory order"
                )
            } else {
                CreateEntry(
                    "factorycreateorder",
                    {
                        id: values?.id || orderMarkDetails?.orderId || 0,
                        ...commonFactoryOrderData,
                    },
                    setMarkCreateMessage,
                    "Factory order created successfully",
                    "Failed to create factory order"
                )
            }
        } else {
            CreateEntry("orderproductmark", commonMarkData, setMarkCreateMessage, "Product submitted successfully", "Failed to submit product")
        }
    }

    const handleSave = () => {
        if (CheckNull(values?.id) !== "") {
            CreateProductOrder();
        } else {
            setProductSubmit(true);
        }
    };

    const SuccessDoneClick = () => {
        setSuccessShow(false);
        setMarkCreateMessage(null);
        setOrderProductTab({ tab: "ProductList", id: 0, status: "updated" });
    };

    const goBack = () => {
        setOrderProductTab({ tab: "" });
    };

    const handleImgError = (e) => {
        e.target.onerror = null;
        e.target.src = fallbackImg;
    };

    const groupedSpecs = configSpecs.reduce((acc, spec) => {
        const group = spec?.groupName || "MODEL CONFIG";
        if (!acc[group]) acc[group] = [];
        acc[group].push(spec);
        return acc;
    }, {});

    return (
        <div className="order-configuration">
            <div className="card">
                <div className="card-body">
                    <div className="order-tab-header-card d-flex align-items-center justify-content-between mb-0">
                        <div className="d-flex align-items-center">
                            <button className="angle-left" onClick={goBack} style={{ cursor: "pointer" }}>
                                <i className="fas fa-angle-left" title="Back"></i>
                            </button>
                            <nav>
                                <div className="nav nav-tabs" id="nav-tab" role="tablist">
                                    <button
                                        className={`nav-link${activeTab === "nav-configuration" ? " active" : ""}`}
                                        id="nav-configuration-tab"
                                        type="button"
                                        role="tab"
                                        aria-controls="nav-configuration"
                                        aria-selected={activeTab === "nav-configuration"}
                                        onClick={() => setActiveTab("nav-configuration")}
                                    >
                                        Configuration
                                    </button>
                                    <button
                                        className={`nav-link${activeTab === "nav-sizes" ? " active" : ""}`}
                                        id="nav-sizes-tab"
                                        type="button"
                                        role="tab"
                                        aria-controls="nav-sizes"
                                        aria-selected={activeTab === "nav-sizes"}
                                        onClick={() => setActiveTab("nav-sizes")}
                                    >
                                        Sizes
                                    </button>
                                </div>
                            </nav>
                        </div>
                        {showPendingApprovalLockMessage && (
                            <span className="mx-3 text-muted">
                                Factory release cannot be amended while approval is pending.
                            </span>
                        )}
                        <button
                            type="button"
                            className="btn update-btn"
                            onClick={handleSave}
                            disabled={isEditLocked}
                        >
                            <i className="fas fa-save mr-1"></i>
                            {isFactoryOrderEdit ? "Update" : "Save"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="tab-content" id="nav-tabContent">

                <div className={`tab-pane fade${activeTab === "nav-sizes" ? " show active" : ""}`} id="nav-sizes" role="tabpanel" aria-labelledby="nav-sizes-tab">
                    <ProductQuantityTable
                        orderMarkDetails={orderMarkDetails}
                        currencyDetail={currencyDetail}
                        addProduct={addProduct}
                        variantsArray={variantsArray}
                        productConfigVariant={productConfigVariant}
                        setAddProduct={setAddProduct}
                        markErrorShow={markErrorShow}
                        setMarkErrorShow={setMarkErrorShow}
                        values={values}
                        factoryOrderRecord={factoryOrderRecord}
                        isFactoryOrderEdit={isFactoryOrderEdit}
                        isFactoryOrderReleased={isFactoryOrderReleased}
                        isFactoryOrderLocked={isEditLocked}
                        releaseForm={releaseForm}
                    />
                </div>

                <div className={`tab-pane fade${activeTab === "nav-configuration" ? " show active" : ""}`} id="nav-configuration" role="tabpanel" aria-labelledby="nav-configuration-tab">
                    <div className="row">
                        <div className="col-xxl-9 col-xl-9 col-lg-8 col-md-12 configuration-col mt-2">
                            <div className="card">
                                <div className="card-body">
                                    <div>
                                        <h5>MODEL CONFIGURATION</h5>
                                        <span>{configSpecs?.length || 0} configured</span>
                                    </div>
                                </div>
                                <table>
                                    {Object.entries(groupedSpecs).map(([groupName, specs]) => (
                                        <React.Fragment key={groupName}>
                                            <thead>
                                                <tr>
                                                    <th colSpan="3">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <h2>{groupName}</h2>
                                                            <p>{specs.length} field{specs.length !== 1 ? "s" : ""}</p>
                                                        </div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {specs.map((spec, index) => (
                                                    <tr key={spec?.id || spec?.specificationId}>
                                                        <td className="slno">{index + 1}</td>
                                                        <td className="label">{spec?.attributeName}</td>
                                                        <td>
                                                            <span className="config-value-text">
                                                                {spec?.attributeValue === "Custom Colour"
                                                                    ? spec?.specialValue
                                                                    : (spec?.attributeValue || spec?.specialValue || "-")}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </React.Fragment>
                                    ))}
                                </table>
                            </div>
                        </div>
                        <div className="col-xxl-3 col-xl-3 col-lg-4 col-md-12 selected-model-col mt-2">
                            <div className="card mb-1">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-12">
                                            <h2>Selected Model : <span>{orderProductTab?.data?.productName}</span></h2>
                                            <div className="d-flex align-items-center justify-content-between mb-1 gap">
                                                <img
                                                    src={orderProductTab?.data?.productImage || fallbackImg}
                                                    onError={handleImgError}
                                                    alt={orderProductTab?.data?.productName}
                                                />
                                                <div className="product-details">
                                                    {configSpecs?.map((spec) => (
                                                        <span key={spec?.id || spec?.specificationId}>
                                                            {spec?.attributeName}: <strong>{spec?.attributeValue || spec?.specialValue || "-"}</strong> ›
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion" id="accordionExample">
                                <div className="card mb-1">
                                    <div className="card-header" id="headingOne">
                                        <h2 className="mb-0">
                                            <button className="btn btn-link" type="button"
                                                data-toggle="collapse"
                                                data-target="#collapseOne"
                                                aria-expanded="true"
                                                aria-controls="collapseOne">
                                                Product Data
                                            </button>
                                        </h2>
                                    </div>
                                    <div id="collapseOne"
                                        className="collapse show"
                                        aria-labelledby="headingOne"
                                        data-parent="#accordionExample">
                                        <div className="card-body">
                                            No product data available
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Same "Selected Configuration" detail block used on the Quotation page */}
                            <SelectedConfiguration
                                currencyDetail={currencyDetail}
                                orderProductTab={orderProductTab}
                                attributeDetails={configSpecs}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <SaveSuccessModel
                show={successshow}
                createMsg={markCreateMessage}
                setShow={setSuccessShow}
                SuccessDoneClick={SuccessDoneClick}
            />
        </div>
    );
};

export default FactoryOrderQuantityEntry
